import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import { connect } from 'react-redux';

import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { StyledCellFormatter } from '~src/spacesPlatform/components/ListView/StyledCellWrapper';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { Formatters, Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IMetricDataInfo } from '../models/IMetricInfo';
import * as MetricsSelectors from '../selectors';
import { ConnectedMetricSuspendedIndicator } from './MetricSuspendedIndicator';

export function MetricUpdatedCellFormatter(): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function DateTimeCell(props: BodyCell.Props) {
      return <ConnectedMetricUpdatedCell {...props} />;
    }
  );
}

export interface MetricUpdatedCellStateProps {
  metricData: IMetricDataInfo;
  userCanResumeSuspendedMetric: boolean;
  isSuspended: boolean;
  isOwner: boolean;
}

export interface MetricUpdatedCellOwnProps {
  row: DataGrid.Row;
}

export type MetricUpdatedCellProps = MetricUpdatedCellOwnProps & MetricUpdatedCellStateProps;

// we have a connected metric updated cell so that as metric data comes in and updates, we only re-render the cell,
// not the whole data grid. This is tested in test\src\ts\app\spaces\ListView\DataGridCellsTests.tsx
export class MetricUpdatedCell extends React.PureComponent<MetricUpdatedCellProps> {
  public render() {
    const dateTime = this.props.metricData && this.props.metricData.dataRefreshedTimestamp;
    const updatedTime = new Date(dateTime);
    const isMetricSuspended = this.props.isSuspended;
    let text: string;
    if (isMetricSuspended) {
      text = Messages.metricSuspendedRefreshTimestampPlaceholder();
    } else if (dateTime) {
      text = Formatters.formatLongBestFitRelativeTime(updatedTime);
    } else {
      text = '—';
    }
    const testId = this.props.row.data ? `metric-updated-cell-${this.props.row.data.id}` : 'metric-updated-cell';

    return (
      <span style={{ display: 'flex' }} data-tb-test-id={testId}>
        {this.renderSuspendedIndicator()}
        <span style={textStyle} title={dateTime && !this.props.isSuspended ? L10n.localizedDateTime(updatedTime) : ''}>{text}</span>
      </span>
    );
  }

  private renderSuspendedIndicator(): JSX.Element | null {
    if (this.props.isSuspended) {
      return this.renderTooltip();
    } else {
      return null;
    }
  }

  private renderTooltip = (): JSX.Element | null => {
    return (
      <ConnectedMetricSuspendedIndicator
        metricId={this.props.row.data.id as string}
        metricRefreshedTimestamp={this.props.metricData && this.props.metricData.dataRefreshedTimestamp}
        userCanResumeSuspendedMetric={this.props.userCanResumeSuspendedMetric}
        isOwner={this.props.isOwner}
      />
    );
  };
}

const textStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mapStateToProps: MapStateToProps<MetricUpdatedCellStateProps> = (state: AppState, ownProps: MetricUpdatedCellOwnProps) => {
  const metricData = ownProps.row.data ? MetricsSelectors.selectMetricDataForContentItem(state, ownProps.row.data) : {};
  const isSuspended = ownProps.row.data ? ownProps.row.data.suspended : false;
  const userCanResumeSuspendedMetric = MetricsSelectors.userCanResumeSuspendedMetric(state, ownProps.row.data);
  const isOwner = MetricsSelectors.userIsMetricOwner(state, ownProps.row.data);

  return { metricData, userCanResumeSuspendedMetric, isSuspended, isOwner };
};

export const ConnectedMetricUpdatedCell = connect(mapStateToProps)(MetricUpdatedCell);
